import { FormControlLabel, Switch, withStyles } from "@material-ui/core";
import {
  ResolutionType,
  useScreenStore,
} from "application/contexts/screen/ScreenStore";
import { IActionModalHandler } from "components/actionModal/ActionModal";
import { Keyboard } from "components/keyboard/Keyboard";
import PromiseBottomModal from "components/promiseBottomModal/PromiseBottomModal";
import CartView from "modules/cart/presentation/cartView/CartView";
import PaymentProvider, {
  IPaymentResult,
} from "modules/payment/presentation/PaymentProvider";
import React, { forwardRef, useRef } from "react";
import { cnpjMask, cpfMask } from "utils/utils";
import { ReactComponent as Invoice } from "../../../assets/icons/invoice.svg";
import { TransactionType } from "../domain/models/INfce";
import { IOrder } from "../domain/models/IOrder";
import { Payment } from "../domain/models/Payment";
import styles from "./OrderProvider.module.scss";
import UseOrderProvider from "./UseOrderProvider";
import CustomerIdentification, {
  ICustomerIdentificationHandler,
} from "./customerIdentification/CustomerIdentification";
import { Trans } from "react-i18next";
import CustomerDiscounts, { ICustomerDiscountsHandler } from "./customerDiscounts/CustomerDiscounts";

const PrimarySwitch = withStyles({
  switchBase: {
    "&$checked": {
      color: "var(--primary)",
    },
    "&$checked + $track": {
      backgroundColor: "var(--primary)",
    },
  },
  checked: {},
  track: {},
})(Switch);

interface IOrderProviderProps {}

export interface IOrderProviderRef {
  startPayment(
    order: IOrder,
    recharge?: "Recharge" | "Payment" | "WalletRecharge",
    paymentType?: TransactionType
  ): Promise<IPaymentResult>;
  /** Continua um pagamento aprovado recuperado do app Android (ver RecoverPendingPaymentUseCase) */
  resumePayment(
    order: IOrder,
    payment: Payment,
    recharge?: "Recharge" | "Payment" | "WalletRecharge"
  ): Promise<IPaymentResult>;
}

const OrderProvider: React.ForwardRefRenderFunction<
  IOrderProviderRef,
  IOrderProviderProps
> = (_, ref) => {
  const printReceiptRef = useRef<IActionModalHandler>(null);
  const printNfceRef = useRef<IActionModalHandler>(null);
  const customerRef = useRef<ICustomerIdentificationHandler>(null);
  const customerDiscountsRef = useRef<ICustomerDiscountsHandler>(null);

  const { resolution } = useScreenStore();

  const {
    startPayment,
    paymentProvider,
    rechargePayment,
    documentToNf,
    onpressKeyboard,
    onPressBack,
    includDocument,
    setIncludDocument,
    docError,
    enableNfAction,
    documentOwnerName,
    documentLookupLoading,
    t,
  } = UseOrderProvider(ref, printReceiptRef, printNfceRef, customerRef, customerDiscountsRef);

  return (
    <>
      <PaymentProvider
        ref={paymentProvider}
        rechargePayment={rechargePayment}
      />
      <CartView onSendOrder={startPayment} />
      <PromiseBottomModal
        ref={printReceiptRef}
        confirmTitle={t("order.actions.yes")}
        closeButtonTile={t("order.actions.no")}
        autoCloseInMs={6000}
      >
        <div className={styles.content}>
          <Invoice
            style={{
              width: resolution === ResolutionType.FULL_HD ? 280 : 200,
              height: resolution === ResolutionType.FULL_HD ? 280 : 200,
            }}
          />
          <p
            style={{
              fontSize: resolution === ResolutionType.FULL_HD ? 52 : 36,
            }}
          >
            <Trans i18nKey="order.receipt_modal.question">
              Deseja imprimir <span>o comprovante de pagamento?</span>
            </Trans>
          </p>
        </div>
      </PromiseBottomModal>
      <PromiseBottomModal
        ref={printNfceRef}
        closeButtonTile={t("order.actions.issue")}
        confirmTitle={t("order.actions.issue_and_print")}
        hideCloseButton
        disable={!enableNfAction}
      >
        <div
          className={styles.contentNfce}
          style={{
            width: resolution === ResolutionType.FULL_HD ? 850 : 550,
          }}
        >
          <span>
            <Trans i18nKey="order.nfce_modal.title">
              Emissão de <b>nota fiscal</b>
            </Trans>
          </span>
          <FormControlLabel
            control={
              <PrimarySwitch
                checked={includDocument}
                onChange={() => setIncludDocument((prev) => !prev)}
                color="primary"
              />
            }
            label={t("order.nfce_modal.include_document")}
          />
          <div
            className={styles.documentContainer}
            style={{ opacity: includDocument ? 1 : 0.5 }}
          >
            <div className={styles.inputContainer}>
              <span
                style={{
                  fontSize: resolution === ResolutionType.FULL_HD ? 40 : 28,
                  padding: resolution === ResolutionType.FULL_HD ? 26 : 20,
                }}
              >
                {documentToNf.length === 0
                  ? t("order.nfce_modal.placeholder_document")
                  : ""}
                {documentToNf.length > 11
                  ? cnpjMask(documentToNf)
                  : cpfMask(documentToNf)}
              </span>
              <span>{docError}</span>
            </div>
            <div className={styles.documentOwnerContainer}>
              {documentLookupLoading && (
                <div className={styles.documentOwner}>
                  <span className={styles.ownerLoading}>{t("order.nfce_modal.validating_document")}</span>
                </div>
              )}
              {!documentLookupLoading && documentOwnerName && (
                <div className={styles.documentOwner}>
                  <span className={styles.ownerName}>{documentOwnerName}</span>
                </div>
              )}
            </div>
            <Keyboard
              type="clear"
              onPressBack={onPressBack}
              onKeyPress={onpressKeyboard}
              hideClear
              value={documentToNf}
              disable={!includDocument}
              maxLength={14}
              hideValue
            />
          </div>
        </div>
      </PromiseBottomModal>
      <CustomerIdentification ref={customerRef} />
      <CustomerDiscounts ref={customerDiscountsRef} />
    </>
  );
};
export default forwardRef(OrderProvider);
